import React, { useEffect, useRef, useState } from 'react'
import { connect, useDispatch } from 'react-redux'
import {
  ListItem,
  TableCell,
  TableRow,
  Tooltip,
  List,
  makeStyles,
  IconButton,
  ClickAwayListener,
  Dialog,
  DialogTitle,
  DialogContent,
  Snackbar,
} from '@material-ui/core'
import SettingsIcon from '@material-ui/icons/Settings'

// import EditAccountWidget from '../components/lib/EditAccountWidget'
import DeleteModal from '../components/DeleteModal'
import UserSecurityRole from './UserSecurityRole'
import AccountDialog from '../components/lib/AccountDialog'
import SettingsPopper from '../../lib/components/SettingsPopper'
import { RESET_USER_PASSWORD, UPDATE_USER_ROLE } from '../../redux/data/types'
import config from '../../config'
import actions from '../../redux/actions'
import { hasPermission } from '../../lib/components/form/RestrictedComponent'
import { Alert } from '@material-ui/lab'
import ResetPasswordModal from '../components/ResetPasswordModal'

const useStyles = makeStyles((theme) => ({
  title: {
    fontSize: 'xx-large !important',
  },
  userList: {
    maxHeight: '8rem',
    overflow: 'auto',
  },
  warning: {
    'color': '#721c24',
    '&:hover': {
      backgroundColor: '#f8d7da',
    },
    '&.disabled:hover': {
      backgroundColor: 'lightgray',
    },
  },
  noTopPadding: {
    paddingTop: 0,
  },
}))

const requestStates = {
  NONE: 'NONE',
  PENDING: 'PENDING',
  ERROR: 'ERROR',
  SUCCESS: 'SUCCESS',
}

const UserTableRow = (props) => {
  const {
    user,
    userRole,
    onDelete,
    onEditAccount,
    thisUser,
    updateUserRole,
    clientID,
    securityRoles,
    contentGroups,
    requestError,
    requestPending,
    userNotFound,
  } = props
  const classes = useStyles()

  const [settingsOpen, setSettingsOpen] = useState(false)
  const [showDialog, setShowDialog] = useState(false)
  const [showSnackbar, setShowSnackbar] = useState(false)
  const [passwordResetRequest, setPasswordResetRequest] = useState(requestStates.NONE)

  const dispatch = useDispatch()
  const onPasswordReset = (email) => {
    setPasswordResetRequest(requestStates.PENDING)
    dispatch(
      actions.request.sendRequest({
        headers: { 'wsc-api-key': undefined },
        type: RESET_USER_PASSWORD,
        url: config.Auth0.auth0_change_password_uri,
        postData: {
          client_id: config.Auth0.auth0_client_v2,
          email,
          connection: 'Username-Password-Authentication',
        },
        withResult: (err, res) => {
          if (err) {
            setPasswordResetRequest(requestStates.ERROR)
          } else {
            setPasswordResetRequest(requestStates.SUCCESS)
          }
        },
      })
    )
  }

  if (userNotFound) {
    return null
  }

  const assignedContentGroups = contentGroups.filter((contentGroup) => {
    return contentGroup.GroupUsers.some((groupUser) => groupUser.userId === user.userId)
  })
  let snackMessage = requestPending ? 'In Progress' : 'Success'
  if (requestError) {
    snackMessage = 'Error: ' + requestError
  }

  const handleCloseSnackbar = () => setShowSnackbar(false)
  useEffect(() => {
    if (requestPending || requestError) {
      setShowSnackbar(true)
    }
  }, [requestPending, requestError])

  const dialogs = {
    SECURITY: {
      type: 'SECURITY',
      title: 'User Security Role',
      component: (
        <UserSecurityRole
          userName={user.firstName + ' ' + user.lastName}
          userRole={userRole}
          user={user}
          securityRoles={securityRoles}
          onCancel={() => setShowDialog(false)}
          onSave={(roleID) => {
            updateUserRole({ clientID, userRole: roleID, userID: user.userId })
            setShowDialog(false)
          }}
        />
      ),
    },
    EDIT: {
      type: 'EDIT',
      dialog: (
        <AccountDialog
          isOpen={true}
          onClose={() => setShowDialog(false)}
          editing={user}
          onSubmit={(data) => onEditAccount({ ...data, userID: user.userId })}
        />
      ),
    },
    RESET_PASSWORD: {
      type: 'RESET_PASSWORD',
      dialog: (
        <ResetPasswordModal
          onClose={() => setShowDialog(false)}
          onReset={() => {
            setShowDialog(false)
            onPasswordReset(user.email)
          }}
          user={user}
        />
      ),
    },
    DELETE: {
      type: 'DELETE',
      title: 'Permanently Delete User?',
      dialog: (
        <DeleteModal
          onClose={() => setShowDialog(false)}
          onDelete={() => {
            setShowDialog(false)
            onDelete({ clientID, userID: user.userId })
          }}
        />
      ),
    },
  }

  const settingActions = [
    {
      tooltip: user.userId === thisUser.userId ? 'Cannot assign your own role' : '',
      text: 'Assign Security Role',
      disabled: user.userId === thisUser.userId || !hasPermission('account_securityGroups.update'),
      onClick: () => setShowDialog(dialogs.SECURITY.type),
    },
    // { text: 'Assign Content Groups', onClick: () => setShowDialog(dialogs.CONTENT.type) },
    {
      text: 'Edit',
      disabled: user.userId !== thisUser.userId && !hasPermission('account.any_update'),
      onClick: () => setShowDialog(dialogs.EDIT.type),
    },
    {
      text: 'Reset Password',
      disabled: user.userId !== thisUser.userId && !hasPermission('account.any_resetPassword'),
      onClick: () => setShowDialog(dialogs.RESET_PASSWORD.type),
    },
    {
      text: 'Delete',
      disabled: user.userId === thisUser.userId || !hasPermission('account.any_delete'),
      onClick: () => setShowDialog(dialogs.DELETE.type),
      className: classes.warning,
    },
  ]
  const settingsRef = useRef(null)

  return (
    <TableRow>
      <TableCell>
        <div>
          <div>
            {user.firstName} {user.lastName}
          </div>
          <div>{user.email}</div>
        </div>
      </TableCell>
      <TableCell>
        <List className={classes.userList} dense={true} disablePadding={true}>
          {assignedContentGroups.map((assignedGroup, i) => {
            return (
              <ListItem key={'list item' + i}>
                <Tooltip title={assignedGroup.Group.groupDescription}>
                  <span>{assignedGroup.Group.groupName}</span>
                </Tooltip>
              </ListItem>
            )
          })}
        </List>
      </TableCell>
      <TableCell>
        <Tooltip
          title={
            (userRole && userRole.roleDescription) ||
            `The user's role (${user.userRole}) was not found`
          }
        >
          <span>{(userRole && userRole.roleName) || 'Unknown Role'}</span>
        </Tooltip>
      </TableCell>
      <TableCell padding='checkbox'>
        <ClickAwayListener onClickAway={() => setSettingsOpen(false)}>
          <IconButton onClick={() => setSettingsOpen(true)}>
            <SettingsIcon ref={settingsRef} />
          </IconButton>
        </ClickAwayListener>
      </TableCell>
      <SettingsPopper
        onClose={() => setSettingsOpen(false)}
        parentRef={settingsRef}
        open={settingsOpen}
        actions={settingActions}
      />
      <UserDialog dialog={dialogs[showDialog]} onClose={() => setShowDialog(null)} />
      <Snackbar open={showSnackbar} autoHideDuration={8000} onClose={handleCloseSnackbar}>
        <Alert
          onClose={handleCloseSnackbar}
          severity={requestError ? 'error' : requestPending ? 'info' : 'success'}
        >
          {snackMessage}
        </Alert>
      </Snackbar>
      <PasswordRequestSnack
        status={passwordResetRequest}
        onClose={() => setPasswordResetRequest(requestStates.NONE)}
      />
    </TableRow>
  )
}
const PasswordRequestSnack = (props) => {
  const { status, onClose } = props

  let severity
  let message
  switch (status) {
    case requestStates.SUCCESS:
      severity = 'success'
      message = 'Password reset request sent'
      break
    case requestStates.ERROR:
      severity = 'error'
      message = 'There was a problem sending the password reset message'
      break
    case requestStates.PENDING:
      severity = 'warning'
      message = 'Pending'
      break
    case requestStates.NONE:
    default:
      severity = 'info'
      message = ''
  }
  return (
    <Snackbar open={status !== requestStates.NONE} autoHideDuration={5000} onClose={onClose}>
      <Alert severity={severity}>{message}</Alert>
    </Snackbar>
  )
}

const UserDialog = (props) => {
  const { dialog, onClose } = props
  const classes = useStyles()
  if (!dialog) return null
  if (dialog.dialog) return dialog.dialog
  return (
    <Dialog open={true} onClose={onClose}>
      <DialogTitle>
        <span className={classes.title}>{dialog.title}</span>
      </DialogTitle>
      <DialogContent className={classes.noTopPadding}>{dialog.component}</DialogContent>
    </Dialog>
  )
}

const mapDispatchToProps = (dispatch) => ({
  updateUserRole: ({ clientID, userRole, userID }) => {
    dispatch(
      actions.request.sendRequest({
        type: UPDATE_USER_ROLE,
        url: `${config.uris.crud}Users/updateUser`,
        postData: {
          clientID,
          userRole,
          userID,
        },
      })
    )
  },
})

const mapStateToProps = (state, ownProps) => {
  const { securityRoles, contentGroups } = state.data
  const clientID = state.data.user.clientId
  const userId = ownProps.userId
  const user = state.data.users.find((stateUser) => stateUser.userId === userId)
  if (user === undefined) {
    return { userNotFound: true }
  }
  const userRole = securityRoles.find((role) => role.roleID === user.userRole)
  const userApiRequest = state.request['API_REQUEST_users_edit']
  const securityRoleRequest = state.request[UPDATE_USER_ROLE]
  let requestError =
    (userApiRequest &&
      userApiRequest.pending === false &&
      userApiRequest[0] &&
      userApiRequest[0] !== null) ||
    (securityRoleRequest &&
      securityRoleRequest.pending === false &&
      securityRoleRequest.error &&
      securityRoleRequest.error.message)
  if (requestError && typeof requestError === 'boolean') {
    //@TODO: does this request not have an error message in the response for the user api??
    requestError = 'Request Failed'
  }
  let requestPending =
    (userApiRequest && userApiRequest?.pending) ||
    (securityRoleRequest && securityRoleRequest?.pending)

  return { clientID, securityRoles, contentGroups, userRole, user, requestPending, requestError }
}

export default connect(mapStateToProps, mapDispatchToProps)(UserTableRow)
